import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { roundDownTotal } from './Consumables/consumableHelpers';
import BillInvoicePreview from '../components/BillInvoicePreview';
import InvoicePreviewModal from '../components/InvoicePreviewModal';
import BillViewModal from '../components/BillViewModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Patient {
  id: number;
  patient_name: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  case_start_date: string;
  address: string;
  patient_clients: { client_name: string; contact_number_1: string; email: string }[];
}

interface ReturnableBatch {
  batch_id: number;
  consumable_id: number;
  consumable_name: string;
  gst_percent: number | null;
  batch_no: string | null;
  hsn_sac: string | null;
  mrp: number | null;
  selling_price: number | null;
  dealer_price: number | null;
  current_quantity: number;
  status: string;
  expiry_date: string | null;
  vendor_name: string | null;
}

interface ReturnLineItem {
  batch_id: number | null;
  particulars: string;
  hsn_sac: string;
  return_price: number;
  quantity: number | '';
  gst_percent: number;
  cgst_amount: number;
  sgst_amount: number;
  subtotal: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = new Date().toISOString().split('T')[0];

const fmtAmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_OPTIONS = ['Pending', 'Collected', 'Processed'];

const emptyItem = (): ReturnLineItem => ({
  batch_id: null,
  particulars: '',
  hsn_sac: '',
  return_price: 0,
  quantity: '',
  gst_percent: 0,
  cgst_amount: 0,
  sgst_amount: 0,
  subtotal: 0,
});

const computeDownstream = (item: ReturnLineItem): ReturnLineItem => {
  const qty = Number(item.quantity) || 0;
  const gst_percent = item.gst_percent || 0;
  const taxable_value = item.return_price * qty;
  const cgst = taxable_value * ((gst_percent / 2) / 100);
  const sgst = taxable_value * ((gst_percent / 2) / 100);
  const subtotal = taxable_value + cgst + sgst;
  return { ...item, cgst_amount: cgst, sgst_amount: sgst, subtotal };
};

const batchDisplay = (b: ReturnableBatch) =>
  `${b.consumable_name}${b.batch_no ? ` — Batch #${b.batch_no}` : ''}${b.status !== 'Active' ? ` (${b.status})` : ''}`;

// ─── Main Component ───────────────────────────────────────────────────────────

const CreateConsumableReturn: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [returnableBatches, setReturnableBatches] = useState<ReturnableBatch[]>([]);
  // Credit-note series (CR 1 / FY 2026-27). Read-only: the server assigns the real
  // one on save, so this is a preview that can go stale if someone else returns first.
  const [returnNo, setReturnNo] = useState('');
  const [billDate, setBillDate] = useState(today);
  const [status, setStatus] = useState('Pending');
  const [remarks, setRemarks] = useState('');
  const [lineItems, setLineItems] = useState<ReturnLineItem[]>([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  // Set once the return exists on the server. Until then the preview is a draft and
  // cannot be downloaded — its credit-note number is only a guess at the next one.
  const [createdBillId, setCreatedBillId] = useState<number | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchInitial = useCallback(async () => {
    if (!patientId) return;
    try {
      const [pRes, batchRes] = await Promise.all([
        axios.get(`${API_URL}/api/patients/${patientId}`, { headers }),
        axios.get(`${API_URL}/api/consumables/returnable-batches`, { headers }),
      ]);
      setPatient(pRes.data);
      setReturnableBatches(batchRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  // The FY suffix comes off the bill date, so re-preview whenever it changes.
  useEffect(() => {
    if (!billDate) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/billing/next-consumable-return-no`, { headers, params: { bill_date: billDate } })
      .then((r) => { if (!cancelled) setReturnNo(r.data.invoice_no); })
      .catch(() => { if (!cancelled) setReturnNo(''); });
    return () => { cancelled = true; };
  }, [billDate]);

  // ── line item helpers ───────────────────────────────────────────────────

  const selectBatch = (idx: number, batch: ReturnableBatch) => {
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      return computeDownstream({
        ...item,
        batch_id: batch.batch_id,
        particulars: batchDisplay(batch),
        hsn_sac: batch.hsn_sac ?? '',
        return_price: batch.mrp ?? batch.selling_price ?? 0,
        gst_percent: batch.gst_percent ?? 0,
      });
    }));
  };

  const updateItem = (idx: number, field: keyof ReturnLineItem, value: number | string) => {
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value };

      if (field === 'hsn_sac') {
        return next;
      }
      if (field === 'cgst_amount' || field === 'sgst_amount') {
        const taxable = next.return_price * (Number(next.quantity) || 0);
        next.subtotal = taxable + Number(next.cgst_amount || 0) + Number(next.sgst_amount || 0);
        return next;
      }

      return computeDownstream(next);
    }));
  };

  const removeItem = (idx: number) =>
    setLineItems((prev) => prev.filter((_, i) => i !== idx));

  // ── save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!billDate) { toast.error('Bill date is required'); return; }
    if (lineItems.length === 0) { toast.error('Add at least one item'); return; }
    if (lineItems.some((i) => !i.batch_id)) { toast.error('Select a batch for each row'); return; }
    if (lineItems.some((i) => !i.quantity || Number(i.quantity) <= 0)) {
      toast.error('Enter quantity returned for each row');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/consumables/return`,
        {
          items: lineItems.map((i) => ({ batch_id: i.batch_id, quantity: Number(i.quantity) })),
          reason: `Consumable Return ${returnNo}`,
        },
        { headers },
      );

      const billingPayload = {
        patient_id: patient?.id,
        bill_date: billDate,
        // Returns have no billing period or expected-payment date — the columns are
        // still NOT NULL in the DB, so mirror the bill date into them.
        expected_payment_date: null,
        invoice_period_start: billDate,
        invoice_period_end: billDate,
        status,
        bill_type: 'consumable_return',
        remarks: remarks || null,
        // Monetary amounts are negated — this bill is a credit note that
        // reduces the patient's dues, not a charge.
        line_items: lineItems.map((item, idx) => ({
          s_no: idx + 1,
          particulars: item.particulars || 'Consumable Return',
          rate: item.return_price,
          details: item.hsn_sac ? `HSN/SAC: ${item.hsn_sac}` : '',
          shift_type: null,
          shifts_count: Number(item.quantity),
          total_amount: -item.subtotal,
          is_miscellaneous: false,
          item_start_date: null,
          item_end_date: null,
          has_gst: item.gst_percent > 0,
          gst_percent: item.gst_percent > 0 ? item.gst_percent : null,
          section_type: 'consumables',
        })),
        consumable_items: lineItems.map((item) => ({
          batch_id: item.batch_id!,
          quantity: Number(item.quantity),
        })),
      };

      const res = await axios.post(`${API_URL}/api/billing/bills`, billingPayload, { headers });
      toast.success('Return saved successfully — stock restored');
      // Hand straight over to the saved return so it can be downloaded with the real
      // server-assigned number (closing that modal leaves the screen).
      setShowPreview(false);
      setCreatedBillId(res.data.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create return');
    } finally {
      setSaving(false);
    }
  };

  // The unsaved return is never downloadable — the PDF is taken from the saved
  // bill (BillViewModal) once the server has assigned the real credit-note number.

  // ── computed ─────────────────────────────────────────────────────────────

  const totalExGst = lineItems.reduce((sum, i) => sum + i.return_price * (Number(i.quantity) || 0), 0);
  const totalCgst = lineItems.reduce((s, i) => s + i.cgst_amount, 0);
  const totalSgst = lineItems.reduce((s, i) => s + i.sgst_amount, 0);
  // Payable is floored to whole rupees — see roundDownTotal(). This is the
  // figure shown here, printed on the invoice and stored on the bill.
  const itemsTotal = lineItems.reduce((s, i) => s + i.subtotal, 0);
  const grandTotal = roundDownTotal(itemsTotal);
  // The paise dropped by the floor, shown to the biller so the summary
  // visibly adds up instead of the total silently disagreeing with the parts.
  const roundOff = Math.round((itemsTotal - grandTotal) * 100) / 100;

  // ── styles ────────────────────────────────────────────────────────────────

  const s: Record<string, React.CSSProperties> = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
    backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' },
    title: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: '3px 0 0', fontSize: '0.85rem', color: '#64748b' },
    actions: { display: 'flex', gap: '10px' },
    card: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '1.5rem' },
    sectionTitle: { margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' },
    patientInfo: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' },
    infoLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    infoValue: { fontSize: '0.88rem', color: '#334155', marginTop: '2px' },
    fieldLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    input: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const },
    select: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const, backgroundColor: 'white' },
    table: { width: '100%', borderCollapse: 'collapse' as const, tableLayout: 'fixed' as const, marginBottom: '0.5rem' },
    th: { padding: '0.55rem 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.04em', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' as const },
    td: { padding: '0.4rem 0.4rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' as const },
    cellInput: { width: '100%', padding: '0.38rem 0.45rem', borderRadius: '5px', border: '1px solid #e2e8f0', fontSize: '0.8rem', boxSizing: 'border-box' as const },
    summaryBox: { backgroundColor: '#fef2f2', borderRadius: '8px', padding: '1rem', border: '1px solid #fecaca', minWidth: '300px' },
    summaryLine: { display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.88rem', color: '#64748b' },
    grandTotalLine: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '1.05rem', fontWeight: 700, color: '#b91c1c', borderTop: '2px solid #fecaca', marginTop: '4px' },
    deleteBtn: { padding: '0.28rem 0.45rem', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '0.78rem' },
    textarea: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', resize: 'vertical' as const, minHeight: '80px', boxSizing: 'border-box' as const },
  };

  const btn = (variant: string): React.CSSProperties => ({
    padding: '0.55rem 1.2rem', borderRadius: '8px', cursor: 'pointer',
    fontSize: '0.88rem', fontWeight: 600,
    backgroundColor: variant === 'primary' ? '#b91c1c' : 'white',
    color: variant === 'primary' ? 'white' : '#334155',
    border: variant === 'primary' ? 'none' : '1px solid #cbd5e1',
    opacity: saving ? 0.7 : 1,
  });

  if (loading) return <div style={s.page}><div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: '4rem' }}>Loading…</div></div>;

  const client = patient?.patient_clients?.[0];

  const batchOptions = returnableBatches.map((b) => ({
    value: b.batch_id,
    label: batchDisplay(b),
    batch: b,
  }));

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div>
          <button style={s.backBtn} onClick={() => navigate(patient ? `/billing/patient/${patient.id}` : '/billing')}>
            ← Back
          </button>
          <h2 style={s.title}>New Consumable Return</h2>
          {patient && <p style={s.subtitle}>{patient.patient_name} — CID: LHHS{patient.id}</p>}
        </div>
        <div style={s.actions}>
          <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
          <button style={btn('primary')} onClick={handleSave} disabled={saving || createdBillId !== null}>
            {saving ? 'Creating…' : 'Create Return'}
          </button>
        </div>
      </div>

      {/* Client details */}
      {patient && (
        <div style={s.card}>
          <p style={s.sectionTitle}>Client Details</p>
          <div style={s.patientInfo}>
            {[
              { label: 'Client', value: client?.client_name || '—' },
              { label: 'Contact', value: client?.contact_number_1 || '—' },
              { label: 'CID', value: `LHHS${patient.id}` },
              { label: 'Sex', value: patient.sex || '—' },
              { label: 'Patient', value: patient.patient_name },
              { label: 'Age', value: patient.age || '—' },
              { label: 'Hospital', value: patient.treating_details?.[0]?.hospital || patient.treating_hospital || '—' },
              { label: 'Doctor', value: patient.treating_details?.[0]?.doctor || patient.treating_doctor || '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={s.infoLabel}>{label}</div>
                <div style={{ ...s.infoValue, fontWeight: label === 'Client' ? 700 : undefined }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bill details */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Return Details</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Return No</label>
            <input type="text" style={{ ...s.input, background: '#f3f4f6', cursor: 'not-allowed' }}
              value={returnNo || '—'} readOnly
              title="Assigned automatically when the return is saved" />
            <div style={{ fontSize: '0.72rem', color: '#92400e', marginTop: '0.25rem' }}>
              Provisional — the final number is assigned when you create the return.
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Bill Date</label>
            <input type="date" style={s.input} value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Status</label>
            <select style={s.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Returned Items</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '3%',  textAlign: 'center' }}>#</th>
                <th style={{ ...s.th, width: '24%' }}>Item / Batch</th>
                <th style={{ ...s.th, width: '9%'  }}>HSN/SAC</th>
                <th style={{ ...s.th, width: '9%',  textAlign: 'right' }}>Return Price ₹</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'center' }}>Qty Returned</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'center' }}>GST %</th>
                <th style={{ ...s.th, width: '10%', textAlign: 'right' }}>CGST ₹</th>
                <th style={{ ...s.th, width: '10%', textAlign: 'right' }}>SGST ₹</th>
                <th style={{ ...s.th, width: '11%', textAlign: 'right' }}>Sub Total ₹</th>
                <th style={{ ...s.th, width: '3%'  }}></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>

                  {/* Particulars / Batch */}
                  <td style={s.td}>
                    <Select
                      options={batchOptions}
                      value={item.batch_id ? batchOptions.find((o) => o.value === item.batch_id) ?? null : null}
                      onChange={(opt) => { if (opt) selectBatch(idx, opt.batch); }}
                      placeholder="Search item or batch…"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </td>

                  {/* HSN/SAC */}
                  <td style={s.td}>
                    <input
                      type="text"
                      style={s.cellInput}
                      value={item.hsn_sac}
                      onChange={(e) => updateItem(idx, 'hsn_sac', e.target.value)}
                      placeholder="HSN"
                    />
                  </td>

                  {/* Return Price ₹ */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.return_price || ''}
                      onChange={(e) => updateItem(idx, 'return_price', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>

                  {/* Qty Returned */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'center' }}
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      placeholder="0"
                      min={1}
                    />
                  </td>

                  {/* GST % */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'center' }}
                      value={item.gst_percent || ''}
                      onChange={(e) => updateItem(idx, 'gst_percent', Number(e.target.value) || 0)}
                      placeholder="0"
                      min={0}
                    />
                  </td>

                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.cgst_amount || ''}
                      onChange={(e) => updateItem(idx, 'cgst_amount', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.sgst_amount || ''}
                      onChange={(e) => updateItem(idx, 'sgst_amount', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: '#b91c1c' }}>
                    {item.subtotal > 0 ? `₹${fmtAmt(item.subtotal)}` : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          onClick={() => setLineItems((p) => [...p, emptyItem()])}
          style={{
            padding: '0.3rem 0.8rem', borderRadius: '5px', cursor: 'pointer',
            fontSize: '0.78rem', fontWeight: 500, border: '1px dashed #fca5a5',
            backgroundColor: '#fef2f2', color: '#b91c1c', marginBottom: '1rem',
          }}
        >
          + Add Row
        </button>

        {grandTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={s.summaryBox}>
              <div style={s.summaryLine}><span>Subtotal (ex-GST)</span><span>₹{fmtAmt(totalExGst)}</span></div>
              <div style={s.summaryLine}><span>Total CGST</span><span>₹{fmtAmt(totalCgst)}</span></div>
              <div style={s.summaryLine}><span>Total SGST</span><span>₹{fmtAmt(totalSgst)}</span></div>
              {roundOff > 0 && (
                <div style={s.summaryLine}><span>Rounded Off</span><span>− ₹{fmtAmt(roundOff)}</span></div>
              )}
              <div style={s.grandTotalLine}><span>Credit Amount</span><span>₹{fmtAmt(grandTotal)}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Remarks */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Remarks</p>
        <textarea
          style={s.textarea}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any additional notes for this consumable return…"
        />
      </div>

      {/* Bottom action row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
        <button style={btn('primary')} onClick={handleSave} disabled={saving || createdBillId !== null}>
          {saving ? 'Creating…' : 'Create Return'}
        </button>
      </div>

      {/* Draft preview — no download until the return is actually created */}
      {showPreview && patient && (
        <InvoicePreviewModal
          draft
          createLabel="Create Return & Download"
          draftNotice="This return has not been created yet. The number shown is provisional — create the return to lock it in, then download."
          onCreate={handleSave}
          creating={saving}
          onClose={() => setShowPreview(false)}
        >
          <BillInvoicePreview
            patient={patient}
            billNo={0}
            invoiceNo={returnNo}
            billDate={billDate}
            periodStart={billDate}
            periodEnd={billDate}
            lineItems={[]}
            consumableItems={lineItems.map((item) => ({
              particulars: item.particulars || 'Consumable Return',
              hsn_sac: item.hsn_sac,
              mrp: null,
              discount_rate: null,
              disc_price: item.return_price,
              quantity: Number(item.quantity) || 0,
              gst_percent: item.gst_percent,
              cgst_amount: item.cgst_amount,
              sgst_amount: item.sgst_amount,
              subtotal: item.subtotal,
            }))}
            remarks={remarks}
            billType="consumable_return"
            createdAt={new Date().toISOString()}
          />
        </InvoicePreviewModal>
      )}

      {/* Saved return — the only place its PDF can be taken from */}
      {createdBillId !== null && patient && (
        <BillViewModal
          billId={createdBillId}
          patient={patient as any}
          onClose={() => navigate(`/billing/patient/${patient.id}`)}
        />
      )}
    </div>
  );
};

export default CreateConsumableReturn;
